import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";

import { addMinutes, formatISO } from "date-fns";
import { format } from "date-fns-tz";
import moment from "moment";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { Color } from "../../../../../../../../../../../components/Form/Button";
import Modal from "../../../../../../../../../../../components/Generic/Modal";
import { MemberTimesheet } from "../../../../../../../../../../../models/members/member.timesheet";
import { TimesheetOptions } from "../../../../../../../../../../../util/enums/members";
import { useActions } from "../../../../hooks/actions";
import { CreatingMockTimesheet } from "../components/CreatingMockTimesheet";
import { ExistentTaskForTimesheet } from "../components/ExistentTaskForTimesheet";
import { TimesheetFirstStep } from "../components/TimesheetFirstStep";
import useStyles, { ModalSection } from "./styles";

export type ManageTimesheeetHandles = {
  open: (root?: MemberTimesheet, onView?: boolean) => void;
  close: () => void;
};

type Props = {
  isLoading?: boolean;
  onSubmitTimesheet: (data: MemberTimesheet | null) => void;
};

const ManageTimesheeetComponent: ForwardRefRenderFunction<ManageTimesheeetHandles, Props> = (
  props,
  ref
) => {
  const { isLoading = false, onSubmitTimesheet } = props;
  const { t: translate } = useTranslation();
  const classes = useStyles();
  const { timesheets, setTimesheets, setTimesheetErrors } = useActions();

  const [isOpened, setIsOpened] = useState(false);
  const [nextStep, setNextStep] = useState<string | null>(null);
  const [isOnView, setIsOnView] = useState<boolean>(false);

  const [option, setOption] = useState<string | null>(null);

  const onResetFields = useCallback(() => {
    setTimesheetErrors([]);
    setNextStep(null);
    setOption(null);
    setTimesheets(null);
  }, []);

  const onOpenModal = useCallback((root?: MemberTimesheet, onView?: boolean) => {
    setIsOpened(true);
    setIsOnView(onView || false);
    if (root) {
      setTimesheets(root);
      if (root?.taskId) {
        setNextStep(TimesheetOptions.EXISTENT_OPTION);
      } else if (root?.taskType) {
        setNextStep(TimesheetOptions.MOCK_OPTION);
      }
    }
  }, []);

  const onCloseModal = useCallback(() => {
    onResetFields();
    setIsOpened(false);
  }, []);

  const renderContent = () => {
    switch (nextStep) {
      case TimesheetOptions.MOCK_OPTION:
        return <CreatingMockTimesheet isOnView={isOnView} isLoading={isLoading} />;
      case TimesheetOptions.EXISTENT_OPTION:
        return <ExistentTaskForTimesheet isOnView={isOnView} isLoading={isLoading} />;
      default:
        return <TimesheetFirstStep option={option || ""} setOption={setOption} />;
    }
  };

  const renderTextSave = () => {
    if (nextStep) {
      if (timesheets?.id) {
        return translate("update");
      }

      return translate("create");
    }

    return translate("choose");
  };

  function isEndDateAfterThenStartDate(startDateString?: string, endDateString?: string) {
    if (startDateString && endDateString) {
      const startDate = moment(startDateString);
      const endDate = moment(endDateString);

      return endDate.isAfter(startDate);
    }

    return false;
  }

  const formatedHourValue = (value?: any) => {
    if (value) {
      return format(new Date(value), "HH:mm aa");
    }

    return "";
  };

  const schema = yup.object().shape({
    nextStep: yup.string().nullable(),
    taskType: yup.string().when("nextStep", {
      is: (nextStep: string) =>
        nextStep.toLowerCase() === TimesheetOptions.MOCK_OPTION.toLowerCase(),
      then: yup
        .string()
        .required(translate("thisFieldIsRequired"))
        .typeError(translate("thisFieldIsRequired")),
      otherwise: yup.string().nullable(),
    }),
    taskId: yup.number().when("nextStep", {
      is: (nextStep: string) =>
        nextStep.toLowerCase() === TimesheetOptions.EXISTENT_OPTION.toLowerCase(),
      then: yup
        .number()
        .required(translate("thisFieldIsRequired"))
        .typeError(translate("thisFieldIsRequired")),
      otherwise: yup.number().nullable(),
    }),
    arrivedAtTheResidence: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    breakFrom: yup.string().when("hasBreak", {
      is: true,
      then: yup
        .string()
        .required(translate("thisFieldIsRequired"))
        .typeError(translate("thisFieldIsRequired"))
        .test(
          "isArrivedAtTheResidenceEarliestTime",
          `${translate("timeMustBeAfter")} ${formatedHourValue(timesheets?.arrivedAtTheResidence)}`,
          () =>
            isEndDateAfterThenStartDate(
              String(timesheets?.arrivedAtTheResidence),
              String(timesheets?.breakFrom)
            )
        ),
      otherwise: yup.string().nullable(),
    }),
    backFromBreak: yup.string().when("hasBreak", {
      is: true,
      then: yup
        .string()
        .required(translate("thisFieldIsRequired"))
        .typeError(translate("thisFieldIsRequired"))
        .test(
          "isArrivedAtTheResidenceEarliestTime",
          `${translate("timeMustBeAfter")} ${formatedHourValue(timesheets?.breakFrom)}`,
          () =>
            isEndDateAfterThenStartDate(
              String(timesheets?.breakFrom),
              String(timesheets?.backFromBreak)
            )
        ),
      otherwise: yup.string().nullable(),
    }),
    leavingTheResidence: yup.string().when("hasBreak", {
      is: true,
      then: yup
        .string()
        .required(translate("thisFieldIsRequired"))
        .typeError(translate("thisFieldIsRequired"))
        .test(
          "isArrivedAtTheResidenceEarliestTime",
          `${translate("timeMustBeAfter")} ${formatedHourValue(timesheets?.backFromBreak)}`,
          () =>
            isEndDateAfterThenStartDate(
              String(timesheets?.backFromBreak),
              String(timesheets?.leavingTheResidence)
            )
        ),
      otherwise: yup
        .string()
        .required(translate("thisFieldIsRequired"))
        .typeError(translate("thisFieldIsRequired"))
        .test(
          "isArrivedAtTheResidenceEarliestTime",
          `${translate("timeMustBeAfter")} ${formatedHourValue(timesheets?.arrivedAtTheResidence)}`,
          () =>
            isEndDateAfterThenStartDate(
              String(timesheets?.arrivedAtTheResidence),
              String(timesheets?.leavingTheResidence)
            )
        ),
    }),
  });

  const onValidateTimesheet = () => {
    const hasBreak = !!(
      timesheets?.backFromBreak?.toString().length || timesheets?.breakFrom?.toString().length
    );

    try {
      schema.validateSync({ nextStep, ...timesheets, hasBreak }, { abortEarly: false });

      setTimesheetErrors([]);

      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        setTimesheetErrors(error.inner);

        return false;
      }

      return false;
    }
  };

  const getTimezoneOffset = (date: Date) => {
    const offsetInMinutes = date?.getTimezoneOffset();
    const offsetInHours = -offsetInMinutes / 60;

    return offsetInHours;
  };

  const removeUTC = (date: Date | string): string => {
    if (typeof date === "object") {
      const offsetInMinutes = getTimezoneOffset(date) * 60;
      const utcDate = addMinutes(date, offsetInMinutes);

      return new Date(formatISO(utcDate, { representation: "complete" })).toISOString();
    }

    return date;
  };

  const onSubmit = () => {
    if (!nextStep && option) {
      setNextStep(option);
    } else if (onValidateTimesheet()) {
      const data = {
        ...timesheets,
        arrivedAtTheResidence: removeUTC(timesheets?.arrivedAtTheResidence!),
        breakFrom: timesheets?.breakFrom ? removeUTC(timesheets?.breakFrom) : undefined,
        backFromBreak: timesheets?.backFromBreak
          ? removeUTC(timesheets?.backFromBreak)
          : timesheets?.backFromBreak,
        arrivedBackToTheWarehouse: removeUTC(timesheets?.arrivedBackToTheWarehouse!),
        leavingTheResidence: removeUTC(timesheets?.leavingTheResidence!),
      } as MemberTimesheet;

      onSubmitTimesheet(data);
    }
  };

  const modalTitle = useMemo(() => {
    if (nextStep) {
      if (timesheets?.id) {
        if (isOnView) return translate("viewTime");

        return translate("editTime");
      }

      return translate("addTime");
    }
  }, [nextStep, timesheets]);

  useImperativeHandle(ref, () => ({
    open: onOpenModal,
    close: onCloseModal,
  }));

  return (
    <Modal
      open={isOpened}
      handleClose={nextStep ? onCloseModal : undefined}
      title={modalTitle}
      showActionButtons={!isOnView}
      actionButtonProps={{
        dataCyCancel: "cancelButton",
        dataCySave: "submitChangesButton",
        customColorCancel: Color.TEXT_INFO,
        loading: isLoading,
        textSave: renderTextSave(),
        onCancel: onCloseModal,
        onSave: onSubmit,
      }}
      showStatusField={false}
      className={classes.modal}
    >
      <ModalSection>{renderContent()}</ModalSection>
    </Modal>
  );
};

const ManageTimesheeet = forwardRef(ManageTimesheeetComponent);

export default ManageTimesheeet;
